import { Alert, Divider, Grid2 as Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { createTenant } from '../../api/admin/endpoints';
import type { BillingCompanyInput, CreateTenantInput, TenantDetail } from '../../api/admin/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { LicenceFields } from '../../components/LicenceFields';
import {
  emptyLicenceOrderDraft,
  licenceOrderInputFrom,
  type LicenceOrderDraft,
} from '../../components/licenceOrderDraft';
import { useT } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/sk';

import { QuotePreview } from './QuotePreview';

type CreateTenantDialogProps = { onClose: () => void; onCreated: (tenant: TenantDetail) => void };

type CompanyDraft = Required<Omit<BillingCompanyInput, 'country'>> & { country: string };

const EMPTY_COMPANY: CompanyDraft = {
  name: '',
  registrationNumber: '',
  taxNumber: '',
  vatNumber: '',
  street: '',
  city: '',
  postalCode: '',
  country: 'SK',
};

const COMPANY_FIELDS: Array<{ key: keyof CompanyDraft; label: MessageKey; required: boolean; size: number }> =
  [
    { key: 'name', label: 'field.legalName', required: true, size: 12 },
    { key: 'street', label: 'field.street', required: true, size: 6 },
    { key: 'postalCode', label: 'field.postalCode', required: true, size: 2 },
    { key: 'city', label: 'field.city', required: true, size: 3 },
    { key: 'country', label: 'field.country', required: true, size: 1 },
    { key: 'registrationNumber', label: 'field.registrationNumber', required: true, size: 4 },
    { key: 'taxNumber', label: 'field.taxNumber', required: false, size: 4 },
    { key: 'vatNumber', label: 'field.vatNumber', required: false, size: 4 },
  ];

/**
 * A business, its owner, its billing company and its first licence order in one step (US-01, R0006 Q-04).
 * The owner is invited by mail; the licence awaits payment of the proforma issued with it.
 */
export const CreateTenantDialog = ({ onClose, onCreated }: CreateTenantDialogProps) => {
  const t = useT();
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [company, setCompany] = useState<CompanyDraft>(EMPTY_COMPANY);
  const [order, setOrder] = useState<LicenceOrderDraft>(emptyLicenceOrderDraft);
  const [problem, setProblem] = useState<string | null>(null);
  const orderInput = licenceOrderInputFrom(order);
  const mutation = useAppMutation(
    (input: CreateTenantInput) => createTenant(input),
    ['tenants', 'invoices', 'audit'],
    onCreated,
  );

  const submit = (): void => {
    if (orderInput === null) {
      setProblem(t('licence.orderInvalid'));

      return;
    }

    setProblem(null);
    mutation.mutate({
      name: name.trim(),
      contactEmail: contactEmail.trim() || undefined,
      owner: { email: ownerEmail.trim(), displayName: ownerName.trim() || undefined },
      billingCompany: {
        name: company.name.trim(),
        registrationNumber: company.registrationNumber.trim(),
        taxNumber: company.taxNumber.trim() || undefined,
        vatNumber: company.vatNumber.trim() || undefined,
        street: company.street.trim(),
        city: company.city.trim(),
        postalCode: company.postalCode.trim(),
        country: company.country.trim().toUpperCase() || undefined,
      },
      licence: orderInput,
    });
  };

  return (
    <FormDialog
      title={t('tenants.create')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('common.create')}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
      maxWidth="md"
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label={t('field.tenantName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            type="email"
            label={t('field.contactEmail')}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </Grid>
      </Grid>
      <Divider />
      <Typography variant="subtitle2">{t('tenants.owner')}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            type="email"
            label={t('field.email')}
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('field.displayName')}
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
          />
        </Grid>
      </Grid>
      <Alert severity="info" variant="outlined">
        {t('invitation.hint')}
      </Alert>
      <Divider />
      <Typography variant="subtitle2">{t('tenants.billingCompany')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('tenants.billingCompanyHint')}
      </Typography>
      <Grid container spacing={2}>
        {COMPANY_FIELDS.map((field) => (
          <Grid key={field.key} size={{ xs: 12, sm: field.size }}>
            <TextField
              fullWidth
              required={field.required}
              label={t(field.label)}
              value={company[field.key]}
              onChange={(e) => setCompany({ ...company, [field.key]: e.target.value })}
            />
          </Grid>
        ))}
      </Grid>
      <Divider />
      <Typography variant="subtitle2">{t('tenants.firstOrder')}</Typography>
      <TextField
        required
        label={t('field.months')}
        value={order.months}
        inputProps={{ inputMode: 'numeric' }}
        onChange={(e) => setOrder({ ...order, months: e.target.value })}
        sx={{ maxWidth: 200 }}
      />
      <LicenceFields
        draft={order}
        onChange={(next) => setOrder({ ...order, ...next })}
        showNotes
        showDates={false}
      />
      <QuotePreview order={orderInput} />
    </FormDialog>
  );
};
